<script lang="ts" setup>
import { tpgIconCircleFilledBlue, tpgIconPushPin } from '@tpg/grammar-web-icons';
import { storeToRefs } from 'pinia';
import {
  computed,
  ref,
  toRefs,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';

import { GrammarIcon, GrammarQuaternaryButton } from '@nora-monorepo/grammar-ui';
import type { ConversationListResultDTO } from '@nora-monorepo/nora-api-client';
import {
  AvatarImage,
  bgSecondary,
  borderSeparatorTransparency,
  focusBgTertiary,
  getIconOrIntitials,
  textBlue,
  textPrimary,
  textSecondary,
  textTertiary,
  useDeviceStore,
} from '@nora-monorepo/ui-shared';

import ConversationListItemMenu from '#nora-chat-ui/components/sidebar/sidebar-items/ConversationListItemMenu.vue';
import { urlGuardClickHandler } from '#nora-chat-ui/directives/url-guard/url-guard.directive';
import type { ConversationState } from '#nora-chat-ui/states';
import { useConversationStore } from '#nora-chat-ui/stores/conversation/conversation-store';
import { useScrollStore } from '#nora-chat-ui/stores/scroll-store';
import { useChatUserStore } from '#nora-chat-ui/stores/user-store';
import { getRelativeTimestamp } from '#nora-chat-ui/utils/get-relative-timestamp';
import { highlight as vHighlight } from '#nora-chat-ui/utils/highlight';
import { renderMessagePreview } from '#nora-chat-ui/utils/message-markdown';

const props = defineProps<Props>();

const { t } = useI18n();

interface Props {
  conversationState: ConversationState;
  highlightText?: string;
}

const { conversationState, highlightText } = toRefs(props);

const userStore = useChatUserStore();
const conversationStore = useConversationStore();
const scrollStore = useScrollStore();
const deviceStore = useDeviceStore();

const icon = ref('');
const initials = ref('');

const { isMobile } = storeToRefs(deviceStore);

const { getConversationTitle, getVisibleConversations }
  = storeToRefs(conversationStore);

const conversation = computed<ConversationListResultDTO>(
  () => props.conversationState.conversationListResult,
);

watch(
  [conversation, userStore.user],
  () => {
    const { icon: newIcon, initials: newInitials } = getIconOrIntitials(
      conversation.value,
      userStore.user,
    );

    icon.value = newIcon ?? '';
    initials.value = newInitials ?? '';
  },
  { immediate: true },
);

const conversationIsOpen = computed(() => {
  if (isMobile.value) {
    return false;
  }

  return getVisibleConversations.value.some(
    openConversation =>
      openConversation.conversationListResult.id
      === conversationState.value.conversationListResult.id,
  );
});

const newestMessage = computed(() => {
  return conversationState.value.conversationListResult.newestMessage;
});

// Optimistically show the conversation as read before waiting for the message read status to finish
const showAsUnread = computed((): boolean => {
  if (conversationState.value.conversationListResult.isRead) {
    return false;
  }

  if (conversationState.value.openIndex == null) {
    return true;
  }

  const hasNewContent = scrollStore.scrollState[conversationState.value.openIndex]
    ?.showNewContentButton;

  return hasNewContent ?? false;
});

const timeStamp = computed(() => {
  const inputDate = new Date(
    conversationState.value.conversationListResult.lastActivity,
  );

  return getRelativeTimestamp(inputDate);
});

function listItemOnClick() {
  conversationStore.setCurrentConversation(conversation.value);
}

function handleKeydown(event: KeyboardEvent) {
  if (event.key === 'Enter' || event.key === 'Space') {
    listItemOnClick();
  }
}

const messageText = computed((): string => {
  if (!newestMessage.value) {
    return t('conversation.empty');
  }

  if (newestMessage.value.deletedAt) {
    return t('common.deleted');
  }

  let message = newestMessage.value.message;

  if (message === null || message.trim() === '') {
    if (newestMessage.value.attachmentCount > 1) {
      message = t('attachments.files', [newestMessage.value.attachmentCount]);
    }
    else if (newestMessage.value.attachmentCount === 1) {
      message = t('attachments.file');
    }
    else {
      message = t('conversation.empty');
    }
  }
  else {
    message = renderMessagePreview(message);
  }

  return message;
});

const previewTitle = computed(() => {
  if (!newestMessage.value) {
    return messageText.value;
  }

  return `${newestMessage.value.sentBy}: ${messageText.value}`;
});

const conversationTitle = computed(() =>
  getConversationTitle.value(conversationState.value.conversationListResult.id),
);

function onPreviewClick(event: MouseEvent): void {
  if (!(event.target instanceof Element)) {
    return;
  }

  const link = event.target.closest('a');

  if (link instanceof HTMLAnchorElement) {
    event.stopPropagation();
    urlGuardClickHandler(link, event);
  }
}
</script>

<template>
  <div
    class="flex mx-2 h-20 pt-2 cursor-pointer rounded-sm focus:outline-hidden"
    :class="[
      focusBgTertiary,
      {
        [bgSecondary]: conversationIsOpen,
      },
    ]"
    :tabindex="0"
    data-name="conversation-list-item"
    @click="listItemOnClick"
    @keydown="handleKeydown"
  >
    <div v-if="userStore.user" class="pl-1 pr-2 py-3">
      <AvatarImage
        :id="conversationState.conversationListResult.id"
        :text="initials"
        :icon="icon"
        :operation-id="conversation.operationId"
      />
    </div>

    <div
      class="w-full h-full pb-1 pt-1.5 pr-2 flex flex-col border-b max-w-[calc(100%-53px)]"
      :class="borderSeparatorTransparency"
    >
      <div class="flex justify-between gap-2" :class="textPrimary">
        <span
          v-highlight="{
            text: highlightText ?? '',
            class: `${textBlue} font-bold`,
          }"
          class="font-semibold text-ellipsis overflow-hidden whitespace-nowrap mr-1"
          :title="conversationTitle"
        >{{ conversationTitle }}
        </span>
        <div class="inline-flex gap-1 items-center" :class="textSecondary">
          <span
            v-if="timeStamp"
            class="whitespace-nowrap text-sm"
            :class="{
              [`${textBlue} font-semibold`]: showAsUnread,
            }"
          >
            {{ timeStamp }}
          </span>
          <GrammarIcon v-if="showAsUnread" size="xsmall" :icon-template="tpgIconCircleFilledBlue" />
        </div>
      </div>
      <div
        class="flex justify-between items-center gap-2"
        :class="textSecondary"
      >
        <div
          class="items-center line-clamp-2 leading-tight text-sm"
          :class="{
            [`${textPrimary} font-semibold`]: showAsUnread,
          }"
        >
          <!-- eslint-disable vue/no-v-html -->
          <div
            data-name="conversation-list-item-message"
            class="markdown break-anywhere"
            :title="previewTitle"
            @click="onPreviewClick"
          >
            <span v-if="newestMessage">{{ newestMessage.sentBy }}: </span>
            <span v-html="messageText" />
          </div>
          <!-- eslint-enable vue/no-v-html -->
        </div>

        <div class="flex-nowrap flex items-center gap-1">
          <GrammarQuaternaryButton
            v-if="userStore.isPinned(conversation.id)"
            :icon-template="tpgIconPushPin"
            @click.stop="
              conversationStore.toggleConversationPin(conversation.id)
            "
          />

          <span
            v-if="conversation.isMuted"
            class="mi md-16 notifications_off"
            :class="textTertiary"
          />

          <ConversationListItemMenu
            :conversation-state="conversationState"
          />
        </div>
      </div>
    </div>
  </div>
</template>
